import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { DiscoverGrant } from "@/components/grant_suite/grantDiscoverUi";
import {
  buildGrantDiscoverSearchUrl,
  GRANT_DISCOVER_SORT_OPTIONS,
  type GrantDiscoverFacets,
} from "@/hooks/grantDiscover/buildGrantDiscoverSearchUrl";

type UseGrantDiscoverSearchOptions = {
  municipalityId?: number;
  canUseGrantDiscover: boolean;
  profileComplete: boolean;
  onInsights?: (insights: string) => void;
  onRecommendationFiltersChange?: () => void;
};

function appendUniqueGrants(existing: DiscoverGrant[], incoming: DiscoverGrant[]) {
  const seen = new Set(existing.map((grant) => grant.id));
  const appended = incoming.filter((grant) => !seen.has(grant.id));
  return [...existing, ...appended];
}

export function useGrantDiscoverSearch({
  municipalityId,
  canUseGrantDiscover,
  profileComplete,
  onInsights,
  onRecommendationFiltersChange,
}: UseGrantDiscoverSearchOptions) {
  const [query, setQuery] = useState("");
  const [keyword, setKeyword] = useState("");
  const [keywordDebounced, setKeywordDebounced] = useState("");
  const [agency, setAgency] = useState("");
  const [catalogScope, setCatalogScopeState] = useState("");
  const [stateFilter, setStateFilterState] = useState("");
  const [excludeFormula, setExcludeFormulaState] = useState(false);
  const [sort, setSort] = useState<string>("relevance");
  const [page, setPage] = useState(1);
  const pageRef = useRef(page);
  pageRef.current = page;
  const [grants, setGrants] = useState<DiscoverGrant[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [summarizingGrants, setSummarizingGrants] = useState<Record<number, boolean>>({});
  const [grantSummaries, setGrantSummaries] = useState<Record<number, string>>({});
  const [grantErrors, setGrantErrors] = useState<Record<number, string>>({});
  const [facets, setFacets] = useState<GrantDiscoverFacets | null>(null);
  const [facetsLoading, setFacetsLoading] = useState(true);

  useEffect(() => {
    setHasSearched(false);
    setQuery("");
    setSort("relevance");
    setGrants([]);
    setTotalCount(0);
    setError(null);
  }, [municipalityId]);

  useEffect(() => {
    const t = setTimeout(() => setKeywordDebounced(keyword), 300);
    return () => clearTimeout(t);
  }, [keyword]);

  useEffect(() => {
    let cancelled = false;
    setFacetsLoading(true);
    fetch("/grants/facets.json?fields=agency,state,catalog_scope", {
      headers: { Accept: "application/json" },
    })
      .then((r) => (r.ok ? r.json() : {}))
      .then((data) => {
        if (!cancelled) setFacets(data);
      })
      .catch(() => {
        if (!cancelled) setFacets({});
      })
      .finally(() => {
        if (!cancelled) setFacetsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const runSearch = useCallback(
    (opts?: {
      resetPage?: boolean;
      append?: boolean;
      pageOverride?: number;
      queryOverride?: string;
    }) => {
      const p = opts?.pageOverride ?? (opts?.resetPage ? 1 : pageRef.current);
      setError(null);
      const q = (opts?.queryOverride ?? query).trim() || undefined;
      const kw = keywordDebounced.trim() || undefined;

      if (opts?.append) setLoadingMore(true);
      else setLoading(true);

      const url = buildGrantDiscoverSearchUrl({
        query: q,
        keyword: kw,
        agency: agency || undefined,
        catalogScope: catalogScope || undefined,
        state: stateFilter || undefined,
        excludeFormula: excludeFormula || undefined,
        sort: sort !== "relevance" ? sort : undefined,
        page: p,
        municipalityId: canUseGrantDiscover && municipalityId ? municipalityId : undefined,
      });
      fetch(url, { headers: { Accept: "application/json" } })
        .then((r) => r.json())
        .then((data) => {
          const newGrants = data.grants || [];
          const total = data.total_count ?? 0;
          if (data.error) {
            setError(data.error);
          }
          if (data.profile_context?.insights) {
            onInsights?.(data.profile_context.insights);
          }
          setGrants((prev) =>
            opts?.append ? appendUniqueGrants(prev, newGrants) : newGrants,
          );
          setTotalCount(total);
          setPage(p);
        })
        .catch(() => setError("Something went wrong while searching."))
        .finally(() => {
          setLoading(false);
          setLoadingMore(false);
          setHasSearched(true);
        });
    },
    [query, keywordDebounced, agency, catalogScope, stateFilter, excludeFormula, sort, canUseGrantDiscover, municipalityId, onInsights],
  );

  const loadMore = () => runSearch({ append: true, pageOverride: pageRef.current + 1 });
  const hasMore = hasSearched && grants.length < totalCount;

  useEffect(() => {
    if (!hasSearched) return;
    runSearch({ resetPage: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when filter inputs change
  }, [keywordDebounced, agency, catalogScope, stateFilter, excludeFormula, sort]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector("textarea");
    const queryValue = (textarea as HTMLTextAreaElement | null)?.value?.trim() ?? query.trim();
    if (!queryValue) return;
    setQuery(queryValue);
    setHasSearched(true);
    runSearch({ resetPage: true, queryOverride: queryValue });
  };

  const handleSummarizeEligibility = async (grant: DiscoverGrant) => {
    if (!grant.eligibility) return;
    setGrantErrors((prev) => {
      const next = { ...prev };
      delete next[grant.id];
      return next;
    });
    setSummarizingGrants((prev) => ({ ...prev, [grant.id]: true }));
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content");
      const response = await fetch("/api/v1/completions/summarize_grant_eligibility", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": token || "",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          grant_eligibility_summary: {
            text: `Grant: ${grant.name} (Program #${grant.program_number})\n\nEligibility Requirements:\n${grant.eligibility}`,
          },
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        setGrantSummaries((prev) => ({ ...prev, [grant.id]: data.response ?? "" }));
      } else {
        const msg = data?.error ?? `Request failed (${response.status})`;
        setGrantErrors((prev) => ({ ...prev, [grant.id]: msg }));
      }
    } catch (err) {
      console.error("Failed to summarize eligibility:", err);
      setGrantErrors((prev) => ({
        ...prev,
        [grant.id]: err instanceof Error ? err.message : "Network error",
      }));
    } finally {
      setSummarizingGrants((prev) => ({ ...prev, [grant.id]: false }));
    }
  };

  const setCatalogScope = (value: string) => {
    if (value !== catalogScope) onRecommendationFiltersChange?.();
    setCatalogScopeState(value);
  };

  const setStateFilter = (value: string) => {
    if (value !== stateFilter) onRecommendationFiltersChange?.();
    setStateFilterState(value);
  };

  const setExcludeFormula = (value: boolean) => {
    if (value !== excludeFormula) onRecommendationFiltersChange?.();
    setExcludeFormulaState(value);
  };

  const clearFilters = () => {
    if (catalogScope || stateFilter || excludeFormula) onRecommendationFiltersChange?.();
    setKeyword("");
    setAgency("");
    setCatalogScopeState("");
    setStateFilterState("");
    setExcludeFormulaState(false);
    setPage(1);
  };

  const hasActiveFilters = Boolean(keyword || agency || catalogScope || stateFilter || excludeFormula);

  const activeFilterChips = [
    keyword && { label: `Keyword: ${keyword}`, onRemove: () => setKeyword("") },
    agency && { label: `Agency: ${agency}`, onRemove: () => setAgency("") },
    catalogScope && {
      label: catalogScope === "federal" ? "Federal only" : "Regional only",
      onRemove: () => setCatalogScope(""),
    },
    stateFilter && { label: `State: ${stateFilter}`, onRemove: () => setStateFilter("") },
    excludeFormula && { label: "Formula-based hidden", onRemove: () => setExcludeFormula(false) },
  ].filter(Boolean) as { label: string; onRemove: () => void }[];

  const sortOptions = useMemo(() => {
    const options = [...GRANT_DISCOVER_SORT_OPTIONS];
    if (canUseGrantDiscover && profileComplete) {
      options.unshift({ value: "fit", label: "Best fit" });
    }
    return options;
  }, [canUseGrantDiscover, profileComplete]);

  return {
    query,
    setQuery,
    keyword,
    setKeyword,
    agency,
    setAgency,
    catalogScope,
    setCatalogScope,
    stateFilter,
    setStateFilter,
    excludeFormula,
    setExcludeFormula,
    sort,
    setSort,
    sortOptions,
    facets,
    facetsLoading,
    hasActiveFilters,
    activeFilterChips,
    clearFilters,
    handleSearchSubmit,
    grants,
    totalCount,
    error,
    loading,
    loadingMore,
    hasSearched,
    setHasSearched,
    hasMore,
    loadMore,
    summarizingGrants,
    grantSummaries,
    grantErrors,
    handleSummarizeEligibility,
  };
}
